// Cartera de cheques de clientes (Rodrigo, 2026-10-24). Un cheque recibido no es plata hasta que
// tesorería confirma su cobro: mientras está en cartera o depositado ocupa la línea de crédito del
// cliente; si se protesta, vuelve a ser deuda vencida. Aquí tesorería los deposita (en una cuenta
// de la empresa), confirma el cobro, los protesta o los devuelve a cartera, de a uno o en lote.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, nuevaClaveIdempotencia } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useCuentasEmpresa } from './datosPagos';

type Estado = 'EN_CARTERA' | 'DEPOSITADO' | 'COBRADO' | 'PROTESTADO';
interface Cheque {
  chequeId: string;
  reciboNumero: string;
  cliente: string;
  rut: string | null;
  banco: string | null;
  numero: string | null;
  fechaDocumento: string;
  monto: string;
  estado: Estado;
  estadoDesde: string | null;
  observacion: string | null;
}

const NOMBRE_ESTADO: Record<Estado, string> = {
  EN_CARTERA: 'En cartera',
  DEPOSITADO: 'Depositado',
  COBRADO: 'Cobrado',
  PROTESTADO: 'Protestado',
};
const TONO: Record<Estado, string> = {
  EN_CARTERA: 'tono-info',
  DEPOSITADO: 'tono-aviso',
  COBRADO: 'tono-ok',
  PROTESTADO: 'tono-malo',
};
/** Qué acción mueve un cheque a qué estado, y desde cuáles se puede. */
const ACCIONES: Array<{ a: Estado; nombre: string; desde: Estado[] }> = [
  { a: 'DEPOSITADO', nombre: 'Depositar', desde: ['EN_CARTERA'] },
  { a: 'COBRADO', nombre: 'Confirmar cobro', desde: ['DEPOSITADO'] },
  { a: 'PROTESTADO', nombre: 'Protestado', desde: ['EN_CARTERA', 'DEPOSITADO'] },
  { a: 'EN_CARTERA', nombre: 'Volver a cartera', desde: ['DEPOSITADO', 'PROTESTADO'] },
];
const hoy = () => new Date().toLocaleDateString('sv-SE');

export function CarteraCheques() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<'' | Estado>('');
  const q = useQuery({
    queryKey: ['emp', empresaId, 'cheques', filtro],
    queryFn: () =>
      api.get<Cheque[]>('/tesoreria/cheques', {
        query: { empresaId, ...(filtro ? { estado: filtro } : {}) },
      }),
  });
  // Por defecto, lo que tesorería tiene que gestionar (todo menos lo ya cobrado).
  const filas = (q.data ?? []).filter((c) => filtro || c.estado !== 'COBRADO');
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const elegidos = filas.filter((c) => marcados.has(c.chequeId));
  const total = elegidos.reduce((s, c) => s + Number(c.monto), 0);
  const [accion, setAccion] = useState<Estado | null>(null);
  const gestiona = puede('tesoreria.cheques.gestionar');
  const alternar = (id: string) =>
    setMarcados((m) => {
      const n = new Set(m);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const sumaPor = (e: Estado) =>
    (q.data ?? []).filter((c) => c.estado === e).reduce((s, c) => s + Number(c.monto), 0);
  return (
    <div className="pantalla consulta cartera-cheques" data-pantalla="cheques">
      <h2>Cartera de cheques de clientes</h2>
      <p className="apagado">
        Un cheque ocupa la línea de crédito del cliente hasta que se confirma su cobro. Protestado,
        vuelve a ser deuda vencida.
      </p>
      <div className="resumen-cheques">
        {(['EN_CARTERA', 'DEPOSITADO', 'PROTESTADO'] as Estado[]).map((e) => (
          <div key={e} className={`tarjeta-cheques ${TONO[e]}`}>
            <span className="etiqueta">{NOMBRE_ESTADO[e]}</span>
            <b className="mono">{pesos(String(sumaPor(e)))}</b>
          </div>
        ))}
      </div>
      <div className="acciones-fila">
        <label className="campo-linea">
          Estado{' '}
          <select
            aria-label="Filtrar por estado"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value as '' | Estado);
              setMarcados(new Set());
            }}
          >
            <option value="">Por gestionar (todo menos cobrados)</option>
            {(Object.keys(NOMBRE_ESTADO) as Estado[]).map((e) => (
              <option key={e} value={e}>
                {NOMBRE_ESTADO[e]}
              </option>
            ))}
          </select>
        </label>
        <span className="espaciador" />
        <span className="apagado">
          {elegidos.length} marcado(s) · {pesos(String(total))}
        </span>
        {gestiona &&
          ACCIONES.map((x) => (
            <button
              key={x.a}
              type="button"
              className={x.a === 'COBRADO' ? 'primario' : ''}
              disabled={!elegidos.length || elegidos.some((c) => !x.desde.includes(c.estado))}
              title={`Solo cheques ${x.desde.map((d) => NOMBRE_ESTADO[d].toLowerCase()).join(' o ')}`}
              onClick={() => setAccion(x.a)}
            >
              {x.nombre}
            </button>
          ))}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Cheques de clientes">
        <thead>
          <tr>
            <th />
            <th>Fecha cheque</th>
            <th>Cliente</th>
            <th>Banco</th>
            <th>N° cheque</th>
            <th className="der">Monto</th>
            <th>Estado</th>
            <th>Recibo</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((c) => (
            <tr key={c.chequeId} data-cheque={c.chequeId}>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Marcar cheque ${c.numero ?? ''} de ${c.cliente}`}
                  disabled={!gestiona || c.estado === 'COBRADO'}
                  checked={marcados.has(c.chequeId)}
                  onChange={() => alternar(c.chequeId)}
                />
              </td>
              <td className={c.fechaDocumento > hoy() ? 'texto-aviso' : ''}>
                {fecha(c.fechaDocumento)}
                {c.fechaDocumento > hoy() ? ' · a fecha' : ''}
              </td>
              <td>
                {c.cliente} <span className="mono apagado">{c.rut ?? ''}</span>
              </td>
              <td>{c.banco ?? '—'}</td>
              <td className="mono">{c.numero ?? '—'}</td>
              <td className="der mono">{pesos(c.monto)}</td>
              <td>
                <span className={`insignia ${TONO[c.estado]}`}>{NOMBRE_ESTADO[c.estado]}</span>
                {c.estadoDesde && <span className="apagado"> {fecha(c.estadoDesde)}</span>}
                {c.observacion && <span className="apagado"> · {c.observacion}</span>}
              </td>
              <td className="mono">{c.reciboNumero}</td>
            </tr>
          ))}
          {q.isSuccess && !filas.length && (
            <tr>
              <td colSpan={8} className="vacio">
                No hay cheques {filtro ? NOMBRE_ESTADO[filtro].toLowerCase() : 'por gestionar'}.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {accion && (
        <CambiarEstado
          empresaId={empresaId}
          accion={accion}
          cheques={elegidos}
          alCerrar={() => setAccion(null)}
          alListo={async () => {
            setAccion(null);
            setMarcados(new Set());
            await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'cheques'] });
          }}
        />
      )}
    </div>
  );
}

function CambiarEstado({
  empresaId,
  accion,
  cheques,
  alCerrar,
  alListo,
}: {
  empresaId: string;
  accion: Estado;
  cheques: Cheque[];
  alCerrar: () => void;
  alListo: () => void;
}) {
  const cuentas = useCuentasEmpresa(empresaId);
  const activas = (cuentas.data ?? []).filter((c) => c.activa);
  const [dia, setDia] = useState(hoy());
  const [cuenta, setCuenta] = useState('');
  const [observacion, setObservacion] = useState('');
  const cuentaElegida = cuenta || activas[0]?.id || '';
  const nombre = ACCIONES.find((x) => x.a === accion)!.nombre;
  const total = cheques.reduce((s, c) => s + Number(c.monto), 0);
  const enviar = useMutation({
    mutationFn: () =>
      api.post(
        '/tesoreria/cheques/estado',
        {
          empresaId,
          chequeIds: cheques.map((c) => c.chequeId),
          estado: accion,
          fecha: dia,
          ...(accion === 'DEPOSITADO' ? { cuentaBancariaId: cuentaElegida } : {}),
          ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
        },
        { idempotencia: nuevaClaveIdempotencia('cheques-estado') },
      ),
    onSuccess: alListo,
  });
  const listo =
    !!dia &&
    (accion !== 'DEPOSITADO' || !!cuentaElegida) &&
    (accion !== 'PROTESTADO' || observacion.trim().length >= 3);
  return (
    <Modal
      titulo={`${nombre} · ${cheques.length} cheque(s)`}
      ancho="angosto"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            data-autofoco
            disabled={!listo || enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending ? 'Guardando…' : nombre}
          </button>
        </>
      }
    >
      <p>
        {cheques.length} cheque(s) por <b>{pesos(String(total))}</b>.
        {accion === 'COBRADO' &&
          ' Al confirmar el cobro se libera la línea de crédito del cliente.'}
        {accion === 'PROTESTADO' && ' Vuelve a ser deuda vencida del cliente.'}
      </p>
      <label className="campo">
        <span className="etiqueta">Fecha</span>
        <input
          type="date"
          aria-label="Fecha"
          value={dia}
          onChange={(e) => setDia(e.target.value)}
        />
      </label>
      {accion === 'DEPOSITADO' && (
        <label className="campo">
          <span className="etiqueta">Cuenta de la empresa</span>
          <select
            aria-label="Cuenta de depósito"
            value={cuentaElegida}
            onChange={(e) => setCuenta(e.target.value)}
          >
            {!activas.length && <option value="">Sin cuentas bancarias de la empresa</option>}
            {activas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.texto}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="campo">
        <span className="etiqueta">
          {accion === 'PROTESTADO' ? 'Motivo del protesto' : 'Observación (opcional)'}
        </span>
        <input
          aria-label="Observación"
          maxLength={500}
          value={observacion}
          onChange={(e) => setObservacion(e.target.value.toUpperCase())}
        />
      </label>
      {!!enviar.error && <PanelError error={enviar.error} />}
    </Modal>
  );
}
